import store from '@/store'
import {
  isAuthorized,
  lazyLoadView,
  piloteGlobalOnly,
  piloteOnly,
  rootAdmin,
  staffPlusAndMore,
  supportOnly,
} from '@/router/routing'
import type { Route, RouteConfig } from 'vue-router'
import { catalogRoutes } from '@/contexts/administration/catalog/Catalog.routes'
import MainContent from '@/components/layout/MainContent.vue'
import { ADMINISTRATION_ROUTES } from '@/contexts/administration/Administration.constants'

export const administrationRoutes: RouteConfig[] = [
  catalogRoutes,
  {
    path: 'customers',
    name: ADMINISTRATION_ROUTES.CUSTOMERS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationCustomersView.vue')),
    meta: {
      i18nTitle: 'administration.menu.customers',
      icon: 'user-circle',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: staffPlusAndMore,
      requires_auth: true,
      requires_no_tenant: true,
    },
  },
  {
    path: 'instance',
    name: ADMINISTRATION_ROUTES.INSTANCE,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationInstanceView.vue')),
    meta: {
      i18nTitle: 'administration.menu.instance',
      icon: 'server',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: rootAdmin,
      requires_auth: true,
      requires_no_tenant: true,
      for_partner: true,
    },
  },
  {
    path: 'auth_config',
    name: ADMINISTRATION_ROUTES.AUTHENTICATION,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationAuthenticationView.vue')),
    meta: {
      i18nTitle: 'administration.menu.authentication',
      icon: 'fingerprint',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteGlobalOnly,
      requires_auth: true,
      requires_tenant: false,
      doc_id: 'auth_config',
      feature_id: 'SETTINGS',
    },
  },
  {
    path: 'users',
    name: ADMINISTRATION_ROUTES.USERS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationUsersView.vue')),
    meta: {
      i18nTitle: 'administration.menu.users',
      icon: 'user',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteOnly.concat(supportOnly),
      requires_auth: true,
      requires_tenant: false,
      doc_id: 'user',
      feature_id: 'USER_GROUP',
    },
    children: [
      {
        path: ':id/objects',
        name: ADMINISTRATION_ROUTES.USER_OBJECTS,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationUserObjectsView.vue')),
        meta: {
          title: null,
          icon: 'boxes',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_role: piloteOnly,
          requires_auth: true,
          requires_tenant: true,
          doc_id: 'user_objects',
        },
      },
    ],
  },
  {
    path: 'group',
    name: ADMINISTRATION_ROUTES.GROUPS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationGroupsView.vue')),
    meta: {
      i18nTitle: 'administration.menu.groups',
      icon: 'users',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteOnly,
      requires_auth: true,
      requires_tenant: true,
      doc_id: 'group',
      feature_id: 'USER_GROUP',
    },
    children: [
      {
        path: ':id/objects',
        name: ADMINISTRATION_ROUTES.GROUP_OBJECTS,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationUserObjectsView.vue')),
        meta: {
          title: null,
          icon: 'boxes',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_role: piloteGlobalOnly,
          requires_auth: true,
          requires_tenant: true,
          doc_id: 'group_objects',
        },
      },
    ],
  },
  {
    path: 'functions',
    name: ADMINISTRATION_ROUTES.FUNCTIONS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationFunctionsView.vue')),
    meta: {
      i18nTitle: 'administration.menu.functions',
      icon: 'user-tag',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteGlobalOnly,
      requires_auth: true,
      requires_tenant: true,
      doc_id: 'function',
      feature_id: 'SETTINGS',
    },
  },
  {
    path: 'perimeter_properties',
    name: ADMINISTRATION_ROUTES.PERIMETER_PROPERTIES,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationPerimeterPropertiesView.vue')),
    meta: {
      i18nTitle: 'administration.menu.properties',
      icon: 'id-card',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteGlobalOnly,
      requires_auth: true,
      requires_tenant: true,
      doc_id: 'perimeter_property',
      feature_id: 'SETTINGS',
    },
  },
  {
    path: 'contacts',
    name: ADMINISTRATION_ROUTES.CONTACTS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationContactsView.vue')),
    meta: {
      i18nTitle: 'administration.menu.contacts',
      icon: 'address-book',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteOnly.concat(supportOnly),
      requires_auth: true,
      requires_tenant: true,
      doc_id: 'contact',
      feature_id: 'SETTINGS',
    },
  },
  {
    path: 'scales',
    name: ADMINISTRATION_ROUTES.SCALES,
    component: MainContent,
    meta: {
      i18nTitle: 'administration.menu.scales',
      icon: 'signal',
      multi: true,
      dummy: true,
      requires_auth: true,
      requires_tenant: false,
      requires_role: piloteGlobalOnly,
      is_authorized: (route: Route) => isAuthorized(route),
      doc_id: 'scales',
      feature_id: 'SETTINGS',
    },
    children: [
      {
        path: 'indicator',
        name: ADMINISTRATION_ROUTES.SCALES_INDICATOR,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationScalesView.vue')),
        meta: {
          i18nTitle: 'administration.menu.indicator',
          icon: 'signal',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_role: piloteGlobalOnly,
          requires_auth: true,
          requires_tenant: false,
          doc_id: 'scales',
          feature_id: 'SETTINGS',
        },
      },
      {
        path: 'eval',
        name: ADMINISTRATION_ROUTES.SCALES_EVALUATION,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationScalesView.vue')),
        meta: {
          i18nTitle: 'administration.menu.evaluation',
          icon: 'signal',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_role: piloteGlobalOnly,
          requires_auth: true,
          requires_tenant: false,
          doc_id: 'scales',
          feature_id: 'SETTINGS',
        },
      },
      {
        path: 'risk',
        name: ADMINISTRATION_ROUTES.SCALES_RISK,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationScalesView.vue')),
        meta: {
          i18nTitle: 'administration.menu.risk',
          icon: 'signal',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_role: piloteGlobalOnly,
          requires_auth: true,
          requires_tenant: false,
          doc_id: 'scales',
          feature_id: 'SETTINGS',
        },
      },
      {
        path: 'perimeter',
        name: ADMINISTRATION_ROUTES.SCALES_PERIMETER,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationScalesView.vue')),
        meta: {
          i18nTitle: 'administration.menu.perimeter',
          icon: 'signal',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_role: piloteGlobalOnly,
          requires_auth: true,
          requires_tenant: false,
          doc_id: 'scales',
          feature_id: 'SETTINGS',
        },
      },
      {
        path: 'priority',
        name: ADMINISTRATION_ROUTES.SCALES_PRIORITY,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationScalesView.vue')),
        meta: {
          i18nTitle: 'administration.menu.priority',
          icon: 'signal',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_role: piloteGlobalOnly,
          requires_auth: true,
          requires_tenant: false,
          doc_id: 'scales',
          feature_id: 'SETTINGS',
        },
      },
      {
        path: 'weather',
        name: ADMINISTRATION_ROUTES.SCALES_WEATHER,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationScalesView.vue')),
        meta: {
          i18nTitle: 'administration.menu.weather',
          icon: 'signal',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_role: piloteGlobalOnly,
          requires_auth: true,
          requires_tenant: false,
          doc_id: 'scales',
          feature_id: 'SETTINGS',
        },
      },
      {
        path: 'business_impacts',
        name: ADMINISTRATION_ROUTES.SCALES_BUSINESS_STAKES,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationScalesView.vue')),
        meta: {
          i18nTitle: 'administration.menu.business_stakes',
          icon: 'signal',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_role: piloteGlobalOnly,
          requires_auth: true,
          requires_tenant: false,
          doc_id: 'scales',
          feature_id: 'SETTINGS',
        },
      },
    ],
  },
  {
    path: 'categories',
    name: ADMINISTRATION_ROUTES.CATEGORIES,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationCategoriesView.vue')),
    meta: {
      i18nTitle: 'administration.menu.categories',
      icon: 'briefcase',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: rootAdmin,
      requires_auth: true,
      requires_tenant: false,
      requires_no_tenant: true,
      doc_id: 'category',
    },
  },
  {
    path: 'labels',
    name: ADMINISTRATION_ROUTES.LABELS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationLabelsView.vue')),
    meta: {
      i18nTitle: 'administration.menu.labels',
      icon: 'tags',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteOnly,
      requires_auth: true,
      requires_tenant: true,
      doc_id: 'label',
      feature_id: 'SETTINGS',
    },
  },
  {
    path: 'internal_indicators',
    name: ADMINISTRATION_ROUTES.INTERNAL_INDICATORS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationInternalIndicatorsView.vue')),
    meta: {
      i18nTitle: 'administration.menu.internal_indicators',
      icon: 'chart-line',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteGlobalOnly,
      requires_auth: true,
      requires_tenant: true,
      doc_id: 'internal_indicator',
      feature_id: 'DASHBOARDS',
    },
  },
  {
    path: 'internal_metrics',
    name: ADMINISTRATION_ROUTES.INTERNAL_METRICS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationInternalMetricsView.vue')),
    meta: {
      i18nTitle: 'administration.menu.internal_metrics',
      icon: 'chart-scatter',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteGlobalOnly,
      requires_auth: true,
      requires_tenant: true,
      doc_id: 'internal_metrics',
      feature_id: 'DASHBOARDS',
    },
  },
  {
    path: 'public_api',
    name: ADMINISTRATION_ROUTES.PUBLIC_API,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationPublicApiView.vue')),
    meta: {
      i18nTitle: 'administration.menu.public_api',
      icon: 'key',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteGlobalOnly,
      requires_auth: true,
      requires_tenant: true,
      hidden_for_fp_assessment: true,
      hidden_for_fe_student: true,
      doc_id: 'public_api',
      feature_id: 'INTEGRATION',
    },
  },
  {
    path: 'logs',
    name: ADMINISTRATION_ROUTES.LOGS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationLogsView.vue')),
    meta: {
      i18nTitle: 'administration.menu.logs',
      icon: 'shoe-prints',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteGlobalOnly,
      requires_auth: true,
      requires_tenant: false,
      doc_id: 'log',
    },
  },
  {
    path: 'import_reports',
    name: ADMINISTRATION_ROUTES.IMPORT_REPORTS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationImportsView.vue')),
    meta: {
      i18nTitle: 'administration.menu.reports',
      icon: 'file-import',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteGlobalOnly,
      requires_auth: true,
      requires_tenant: false,
      doc_id: 'import_report',
    },
  },
  {
    path: 'preferences',
    name: ADMINISTRATION_ROUTES.PREFERENCES,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationPreferencesView.vue')),
    meta: {
      i18nTitle: 'administration.menu.preferences',
      icon: 'sliders-h',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteGlobalOnly,
      requires_auth: true,
      requires_tenant: true,
      doc_id: 'preference',
      feature_id: 'SETTINGS',
    },
  },
  {
    path: 'licenses',
    name: ADMINISTRATION_ROUTES.LICENSES,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationLicensesView.vue')),
    meta: {
      i18nTitle: 'administration.menu.licenses',
      icon: 'badge-check',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: store.getters.instance_type !== 'business' && store.getters.isPublic ? [] : piloteGlobalOnly,
      requires_auth: true,
      requires_tenant: true,
      doc_id: 'license',
      feature_id: 'SETTINGS',
    },
  },
  {
    path: 'customer_connectors',
    name: ADMINISTRATION_ROUTES.CUSTOMER_CONNECTORS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationCustomerConnectorsView.vue')),
    meta: {
      i18nTitle: 'administration.menu.connectors',
      icon: 'puzzle-piece',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: piloteOnly,
      requires_auth: true,
      requires_tenant: true,
      hidden_for_fp_assessment: true,
      doc_id: 'engine',
      feature_id: 'INTEGRATION',
    },
    children: [
      {
        path: 'customer_connector/:connector_id/jobs',
        name: ADMINISTRATION_ROUTES.JOBS,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationConnectorJobsView.vue')),
        meta: {
          i18nTitle: 'administration.menu.integration_jobs',
          icon: 'briefcase',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_role: piloteOnly,
          requires_auth: true,
          requires_tenant: true,
          hidden_for_fp_assessment: true,
          doc_id: 'engine_jobs',
        },
      },
    ],
  },
  {
    path: 'connectors',
    name: ADMINISTRATION_ROUTES.CONNECTORS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationConnectorsView.vue')),
    meta: {
      i18nTitle: 'administration.menu.connectors',
      icon: 'tools',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: rootAdmin,
      requires_auth: true,
      requires_no_tenant: true,
      doc_id: 'connector',
    },
  },
  {
    path: 'workers',
    name: ADMINISTRATION_ROUTES.WORKERS,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationIntegrationWorkersView.vue')),
    meta: {
      i18nTitle: 'administration.menu.integration_workers',
      icon: 'cogs',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: rootAdmin,
      requires_auth: true,
      requires_no_tenant: true,
      doc_id: 'worker',
    },
  },
  {
    path: 'properties',
    name: ADMINISTRATION_ROUTES.PROPERTIES,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationMetadataPropertiesView.vue')),
    meta: {
      i18nTitle: 'administration.menu.properties',
      icon: 'file-chart-line',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: rootAdmin,
      requires_auth: true,
      requires_no_tenant: true,
      doc_id: 'keywords',
    },
    children: [],
  },
  {
    path: 'risk-analysis-catalog',
    name: ADMINISTRATION_ROUTES.RISKS,
    component: MainContent,
    meta: {
      i18nTitle: 'administration.menu.risks',
      icon: 'radiation-alt',
      multi: true,
      dummy: true,
      requires_auth: true,
      requires_no_tenant: true,
      requires_role: staffPlusAndMore,
      is_authorized: (route: Route) => isAuthorized(route),
      doc_id: 'risk-catalog',
    },
    children: [
      {
        path: 'risks',
        name: ADMINISTRATION_ROUTES.RISKS_CATALOG,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationRisksView.vue')),
        meta: {
          i18nTitle: 'administration.menu.risks',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_auth: true,
          requires_no_tenant: true,
          requires_role: staffPlusAndMore,
          doc_id: 'risk-catalog',
        },
      },
      {
        path: 'business-value',
        name: ADMINISTRATION_ROUTES.BUSINESS_VALUES_CATALOG,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationBusinessValuesView.vue')),
        meta: {
          i18nTitle: 'administration.menu.business_values',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_auth: true,
          requires_no_tenant: true,
          requires_role: staffPlusAndMore,
          doc_id: 'business-value-catalog',
        },
      },
      {
        path: 'supporting-asset',
        name: ADMINISTRATION_ROUTES.SUPPORTING_ASSETS_CATALOG,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationSupportingAssetsView.vue')),
        meta: {
          i18nTitle: 'administration.menu.supporting_assets',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_auth: true,
          requires_no_tenant: true,
          requires_role: staffPlusAndMore,
          doc_id: 'supporting-asset-catalog',
        },
      },
      {
        path: 'threat',
        name: ADMINISTRATION_ROUTES.THREATS_CATALOG,
        component: () => lazyLoadView(import('@/contexts/administration/AdministrationThreatsView.vue')),
        meta: {
          i18nTitle: 'administration.menu.threats',
          is_authorized: (route: Route) => isAuthorized(route),
          requires_auth: true,
          requires_no_tenant: true,
          requires_role: staffPlusAndMore,
          doc_id: 'threat-catalog',
        },
      },
    ],
  },
  {
    path: 'catalog_sync',
    name: ADMINISTRATION_ROUTES.CATALOG_SYNC,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationCatalogSyncView.vue')),
    meta: {
      i18nTitle: 'administration.menu.sync_configuration',
      icon: 'check',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: rootAdmin,
      requires_auth: true,
      requires_no_tenant: true,
      doc_id: 'catalog_sync',
      feature_id: 'SETTINGS',
    },
  },
  {
    path: 'login-message',
    name: ADMINISTRATION_ROUTES.LOGIN_MESSAGE,
    component: () => lazyLoadView(import('@/contexts/administration/AdministrationLoginMessageView.vue')),
    meta: {
      i18nTitle: 'administration.menu.login_message',
      icon: 'comment-alt-edit',
      is_authorized: (route: Route) => isAuthorized(route),
      requires_role: rootAdmin,
      requires_auth: true,
      requires_no_tenant: true,
    },
  },
]
